import { jsx as _jsx } from "react/jsx-runtime";
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';
import { ROUTES } from '@/core/config/routes';
import { AppShell } from '@/shared/layouts/AppShell';
import { PublicLayout } from '@/shared/layouts/PublicLayout';
import { AuthGuard } from './guards/AuthGuard';
import { LoginPage } from '@/features/auth/LoginPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { ClientesPage } from '@/features/clientes/ClientesPage';
import { ReservasPage } from '@/features/reservas/ReservasPage';
import { ReservaFormPage } from '@/features/reservas/ReservaFormPage';
import { ClasesPage } from '@/features/clases/ClasesPage';
import { EmpleadosPage } from '@/features/empleados/EmpleadosPage';
import { PromotoresPage } from '@/features/promotores/PromotoresPage';
import { DisciplinasNivelesPage } from '@/features/gente/DisciplinasNivelesPage';
import { PagosPage } from '@/features/pagos/PagosPage';
import { LiquidacionesPage } from '@/features/liquidaciones/LiquidacionesPage';
import { AnticiposPage } from '@/features/liquidaciones/AnticiposPage';
import { MisClasesPage } from '@/features/mis-clases/MisClasesPage';
import { TarifasPage } from '@/features/catalogos/TarifasPage';
import { DescuentosPage } from '@/features/catalogos/DescuentosPage';
import { MetodosPagoPage } from '@/features/catalogos/MetodosPagoPage';
import { OrganizacionPage } from '@/features/organizacion/OrganizacionPage';
import { UsuariosAdminPage } from '@/features/admin/UsuariosAdminPage';
import { GruposAdminPage } from '@/features/admin/GruposAdminPage';
import { ParametrosPage } from '@/features/admin/ParametrosPage';
import { ForbiddenPage } from '@/features/errors/ForbiddenPage';
import { NotFoundPage } from '@/features/errors/NotFoundPage';
import { RoleGuard } from './guards/RoleGuard';
const router = createBrowserRouter([
    {
        element: _jsx(PublicLayout, {}),
        children: [
            { path: ROUTES.login, element: _jsx(LoginPage, {}) },
            { path: ROUTES.recuperar, element: _jsx(ForgotPasswordPage, {}) },
            { path: ROUTES.resetear, element: _jsx(ResetPasswordPage, {}) },
            { path: ROUTES.forbidden, element: _jsx(ForbiddenPage, {}) },
        ],
    },
    {
        path: '/',
        element: (_jsx(AuthGuard, { children: _jsx(AppShell, {}) })),
        children: [
            { index: true, element: _jsx(Navigate, { to: ROUTES.dashboard, replace: true }) },
            { path: ROUTES.dashboard, element: _jsx(DashboardPage, {}) },
            {
                path: ROUTES.clientes,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_CLIENTE", children: _jsx(ClientesPage, {}) })),
            },
            {
                path: ROUTES.reservas,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_RESERVA", children: _jsx(ReservasPage, {}) })),
            },
            {
                path: '/reservas/nueva',
                element: (_jsx(RoleGuard, { roles: "ROLE_CREAR_RESERVA", children: _jsx(ReservaFormPage, {}) })),
            },
            {
                path: ROUTES.reservaDetalle(),
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_RESERVA", children: _jsx(ReservaFormPage, {}) })),
            },
            {
                path: ROUTES.clases,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_RESERVA", children: _jsx(ClasesPage, {}) })),
            },
            {
                path: ROUTES.misClases,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_RESERVA", children: _jsx(MisClasesPage, {}) })),
            },
            {
                path: ROUTES.empleados,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_EMPLEADO", children: _jsx(EmpleadosPage, {}) })),
            },
            {
                path: ROUTES.promotores,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_PROMOTOR", children: _jsx(PromotoresPage, {}) })),
            },
            {
                path: ROUTES.gente.disciplinasNiveles,
                element: (_jsx(RoleGuard, { roles: "ROLE_ADMINISTRAR_EMPLEADO", children: _jsx(DisciplinasNivelesPage, {}) })),
            },
            {
                path: ROUTES.pagos,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_PAGO", children: _jsx(PagosPage, {}) })),
            },
            {
                path: ROUTES.liquidaciones,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_LIQUIDACION", children: _jsx(LiquidacionesPage, {}) })),
            },
            {
                path: ROUTES.anticipos,
                element: (_jsx(RoleGuard, { roles: "ROLE_GENERAR_LIQUIDACION", children: _jsx(AnticiposPage, {}) })),
            },
            {
                path: ROUTES.catalogo.tarifas,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_TARIFA", children: _jsx(TarifasPage, {}) })),
            },
            {
                // Compatibilidad: la página vivía en Catálogo. Ahora vive en Gente.
                path: ROUTES.catalogo.niveles,
                element: _jsx(Navigate, { to: ROUTES.gente.disciplinasNiveles, replace: true }),
            },
            {
                path: ROUTES.catalogo.valores,
                element: _jsx(Navigate, { to: ROUTES.gente.disciplinasNiveles, replace: true }),
            },
            {
                path: ROUTES.catalogo.descuentos,
                element: (_jsx(RoleGuard, { roles: "ROLE_ADMINISTRAR_DESCUENTO", children: _jsx(DescuentosPage, {}) })),
            },
            {
                path: ROUTES.catalogo.metodosPago,
                element: (_jsx(RoleGuard, { roles: "ROLE_ADMINISTRAR_SUCURSAL", children: _jsx(MetodosPagoPage, {}) })),
            },
            {
                path: ROUTES.organizacion.sucursales,
                element: (_jsx(RoleGuard, { roles: "ROLE_VER_SUCURSAL", children: _jsx(OrganizacionPage, {}) })),
            },
            {
                path: ROUTES.organizacion.pdv,
                element: (_jsx(RoleGuard, { roles: "ROLE_ADMINISTRAR_SUCURSAL", children: _jsx(OrganizacionPage, {}) })),
            },
            {
                path: ROUTES.organizacion.puntosEncuentro,
                element: (_jsx(RoleGuard, { roles: "ROLE_ADMINISTRAR_SUCURSAL", children: _jsx(OrganizacionPage, {}) })),
            },
            {
                path: ROUTES.admin.usuarios,
                element: (_jsx(RoleGuard, { roles: "ROLE_ADMINISTRAR_USUARIO", children: _jsx(UsuariosAdminPage, {}) })),
            },
            {
                path: ROUTES.admin.grupos,
                element: (_jsx(RoleGuard, { roles: "ROLE_ADMINISTRAR_USUARIO", children: _jsx(GruposAdminPage, {}) })),
            },
            {
                path: ROUTES.admin.parametros,
                element: (_jsx(RoleGuard, { roles: ["ROLE_ADMINISTRAR_SISTEMA", "ROLE_ADMINISTRAR_PARAMETROS"], children: _jsx(ParametrosPage, {}) })),
            },
        ],
    },
    { path: '*', element: _jsx(NotFoundPage, {}) },
]);
export function AppRouter() {
    return _jsx(RouterProvider, { router: router });
}
